// Generates the copy-paste onboarding prompt for AI coding agents.
// The prompt instructs an agent (Cursor, Claude Code, etc.) to connect the
// user's selected integrations using the Kestrel CLI (or MCP server).

import { getIntegration, IntegrationDef } from './integrationCatalog';

// Returns the connect command with secret flags removed: the CLI prompts for
// missing required secrets interactively (hidden input), so the agent never
// handles credential values. File-based flags (e.g. --private-key-file) are
// kept since a file path is not a secret.
export function connectCommandWithoutSecrets(def: IntegrationDef): string | undefined {
  if (!def.cliCommand) return undefined;
  let cmd = def.cliCommand;
  for (const field of def.fields || []) {
    if (!field.secret) continue;
    const flag = field.name.replace(/_/g, '-');
    // Bracketed optional form: [--flag <...>]
    cmd = cmd.replace(new RegExp(`\\s*\\[--${flag} <[^>]+>\\]`, 'g'), '');
    // Plain form: --flag <...> (won't match --flag-file variants)
    cmd = cmd.replace(new RegExp(`\\s*--${flag} <[^>]+>`, 'g'), '');
  }
  return cmd.trim();
}

// Returns just the paste-safe connect command with no flags or placeholders
// (e.g. "kestrel integrations connect neon"). Used where users copy commands
// straight into a terminal: the CLI prints setup instructions and prompts for
// every missing required value, so no flags are needed up front.
export function bareConnectCommand(def: IntegrationDef): string | undefined {
  if (!def.cliCommand) return undefined;
  // "kestrel integrations connect <name>" = first 4 tokens of the template.
  return def.cliCommand.split(/\s+/).slice(0, 4).join(' ');
}

function integrationInstructions(def: IntegrationDef): string {
  const lines: string[] = [];
  lines.push(`### ${def.name}`);
  const cmd = connectCommandWithoutSecrets(def);
  if (cmd) {
    lines.push('```bash');
    lines.push(cmd);
    lines.push('```');
  }
  if (def.docsNote) {
    lines.push(`Note: ${def.docsNote}`);
  }
  if (def.connectionKind === 'token' && def.fields && def.fields.length > 0) {
    const nonSecret = def.fields.filter((f) => !f.secret);
    const secrets = def.fields.filter((f) => f.secret);
    if (nonSecret.length > 0) {
      lines.push(
        `Ask me for: ${nonSecret.map((f) => `\`${f.name}\` (${f.label}${f.required ? '' : ', optional'})`).join(', ')}.`
      );
    }
    if (secrets.length > 0) {
      lines.push(
        `Secrets the CLI will prompt for with hidden input: ${secrets.map((f) => `${f.label} (\`${f.name}\`)`).join(', ')}. Tell me to have ${secrets.length === 1 ? 'it' : 'them'} ready before running the command.`
      );
    }
  }
  if (def.connectionKind === 'oauth') {
    lines.push('This integration requires a browser sign-in. Run the command, then give me the printed URL so I can finish authorization in my browser.');
  }
  if (def.connectionKind === 'cluster') {
    lines.push('This mints an operator token and prints a `helm install` command. Run that helm command against my cluster (confirm kubectl context with me first).');
  }
  return lines.join('\n');
}

export interface AgentPromptOptions {
  serverUrl?: string;
}

export function generateAgentPrompt(selectedKeys: string[], options?: AgentPromptOptions): string {
  const defs = selectedKeys
    .map((k) => getIntegration(k))
    .filter((d): d is IntegrationDef => !!d);

  const base = options?.serverUrl || window.location.origin;

  const sections = defs.map(integrationInstructions).join('\n\n');

  return `You are helping me set up Kestrel (a workflow automation tool for platform engineering teams) by connecting my tools through the Kestrel CLI.

I have already installed the CLI and authenticated it (\`kestrel auth\`) in this environment, so you never need my Kestrel API key.

## Credential rules (important)

- Never ask me to paste secrets (API tokens, private keys, webhook secrets) into this chat, and never put secret values on a command line.
- For integrations that need secret credentials, run the connect command WITHOUT the secret flags — the CLI will prompt for them interactively with hidden input. If your shell can't do interactive input, print the command and ask me to run it in my own terminal; I'll tell you when it's done.
- Non-secret fields (URLs, org names, regions, account IDs) are fine to ask for in chat and pass as flags.
- Never invent or guess credential values.

## Step 1: Verify the CLI is authenticated

\`\`\`bash
kestrel integrations list
\`\`\`

If this fails with an authentication error, stop and ask me to run \`kestrel auth <api-key> --server ${base}\` in my terminal (I can create a key at ${base}/api-keys), then retry.

## Step 2: Connect my integrations

Connect each of the following. Skip anything \`kestrel integrations list\` already shows as connected. After each connect, verify with \`kestrel integrations test <name>\` where supported.

${sections}

## Step 3: Verify

Run \`kestrel integrations list\` again and confirm everything I selected shows as connected. Summarize what was connected and anything that still needs manual action.

Tip: I can also add Kestrel as an MCP server so you can manage integrations and workflows with native tools instead of shell commands:

\`\`\`json
{
  "mcpServers": {
    "kestrel": { "command": "kestrel", "args": ["mcp"] }
  }
}
\`\`\``;
}
